import React, { useState } from 'react';
import { Info, Trash2, Watch, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import Button from '../../ui/Button';
import { colors } from '../../../constants/colors';
import styles from './GoalCard.module.css';

const GoalCard = ({
	goal,
	onDelete,
	onTrack,
	onViewInfo,
	onViewCareTips,
	onWatch,
	// Reorder / priority props (UI only)
	rank,
	isTop5 = false,
	reorderMode = false,
	isDragging = false,
	canMoveUp = false,
	canMoveDown = false,
	onMoveUp,
	onMoveDown,
	dragHandleProps = {},
}) => {
	const { id, name, goal_description, smart_watch = false } = goal;
	const [showConfirm, setShowConfirm] = useState(false);

	const goalNameStyles = {
		color: colors.text?.primary || colors.primaryDark,
		fontWeight: 900,
	};

	const descriptionStyles = {
		color: colors.text?.primary || colors.primaryDark,
	};

	const cardClassName = [
		styles.card,
		reorderMode ? styles.cardReorder : '',
		isDragging ? styles.cardDragging : '',
	]
		.filter(Boolean)
		.join(' ');

	const badgeClassName = [
		styles.rankBadge,
		isTop5 ? styles.rankBadgeTop : styles.rankBadgeRest,
	].join(' ');

	return (
		<div className={cardClassName}>
			{/* Header with rank badge, name and action buttons */}
			<div className={styles.header}>
				{reorderMode && (
					<button
						className={styles.dragHandle}
						aria-label={`Drag to reorder ${name}`}
						title='Drag to reorder'
						{...dragHandleProps}
					>
						<GripVertical size={20} />
					</button>
				)}

				{rank != null && (
					<span className={badgeClassName} aria-label={`Priority ${rank}`}>
						{rank}
					</span>
				)}

				<h3 className={styles.goalName} style={goalNameStyles}>
					{name}
				</h3>

				{reorderMode ? (
					<div className={styles.reorderArrows}>
						<button
							className={styles.arrowButton}
							onClick={onMoveUp}
							disabled={!canMoveUp}
							aria-label={`Move ${name} up`}
							title='Move up'
						>
							<ChevronUp size={18} />
						</button>
						<button
							className={styles.arrowButton}
							onClick={onMoveDown}
							disabled={!canMoveDown}
							aria-label={`Move ${name} down`}
							title='Move down'
						>
							<ChevronDown size={18} />
						</button>
					</div>
				) : (
				<div className={styles.actionButtons}>
					<button
						onClick={onViewInfo}
						className={styles.iconButton}
						aria-label={`View info for ${name}`}
						title='View Info'
					>
						<Info size={25} />
					</button>
					{showConfirm ? (
						<div className={styles.confirmButtons}>
							<button
								className={styles.cancelButton}
								onClick={() => setShowConfirm(false)}
							>
								Cancel
							</button>
							<button
								className={styles.confirmDeleteButton}
								onClick={() => {
									setShowConfirm(false);
									onDelete && onDelete(id);
								}}
							>
								Delete
							</button>
						</div>
					) : (
						<button
							onClick={() => setShowConfirm(true)}
							className={`${styles.iconButton} ${styles.deleteButton}`}
							aria-label={`Delete ${name}`}
							title='Delete Goal'
						>
							<Trash2 size={25} />
						</button>
					)}
				</div>
				)}
			</div>

			{/* Goal description + actions (hidden while reordering) */}
			{!reorderMode && (
			<div className={styles.contentContainer}>
				<div className={styles.description}>
					<p style={descriptionStyles}>
						{goal_description || 'No description available'}
					</p>
				</div>

				{/* Action buttons row */}
				<div className={styles.actionButtonsRow}>
					{smart_watch && onWatch && (
						<button
							onClick={() => onWatch(id)}
							className={styles.watchButton}
							aria-label={`Monitor ${name} with smartwatch`}
							title='Monitor with Smartwatch'
						>
							<Watch size={18} />
							<span>Watch</span>
						</button>
					)}

					<Button
						onClick={() => onTrack && onTrack(id)}
						variant='primary'
						size='small'
						className={styles.trackButton}
						ariaLabel={`Track progress for ${name}`}
					>
						Track
					</Button>

					<button
						onClick={() => onViewCareTips && onViewCareTips(id)}
						className={styles.careTipButton}
						aria-label={`View care tips for ${name}`}
					>
						Care Tips
					</button>
				</div>
			</div>
			)}
		</div>
	);
};

export default GoalCard;
